import './theme.css';
import { SiteHeader } from '@/app/agent/SiteHeader.tsx';
import type { LocalizedNavItem } from '@/app/lib/stage/nav-model.ts';
import type { AgentHeaderProps } from '@/app/lib/stage/StageHeader.tsx';
import type { FC } from 'react';
import { defineMessages, type MessageDescriptor } from 'react-intl';

export interface SiteConfig {
  brandName: string;
  navItems: LocalizedNavItem[];
  defaultChips: MessageDescriptor[];
  /** Page-area mode: 'site' = the latest surface IS the page; 'chat' = a
   *  scrolling transcript with inline surfaces. Same shell either way. */
  mode: 'site' | 'chat';
  /** Site header (brand + menu). Off = minimal canvas: the rail keeps
   *  history, chips keep the standing intents, the dock keeps input.
   *  Template default is off; a branded site turns it on. */
  showHeader: boolean;
  /** Custom header component. Omit for the template's default header
   *  (monogram + name + menu); provide one for a bespoke composition —
   *  it receives resolved nav items + onNavigate, so menu mechanics and
   *  the agent's /nav overlay keep working. */
  Header?: FC<AgentHeaderProps>;
  /** First-load pending state shows the brand name (shimmer) instead of the
   *  neutral text skeleton — for agents with a website identity. */
  brandedArrival?: boolean;
  /** The site's designed look — a builder decision, like a real website's.
   *  'auto' follows the visitor's OS. */
  appearance: 'light' | 'dark' | 'auto';
  /** Omnibox placeholder override. */
  placeholder?: MessageDescriptor;
}

const siteMessages = defineMessages({
  navStart: {
    id: 'site.nav.start',
    defaultMessage: 'Start',
    description: 'Site navigation label: return to the beginning of the five-step trip journey',
  },
  navStartIntent: {
    id: 'site.nav.startIntent',
    defaultMessage: 'Take me to the start of the trip',
    description: 'Message sent when the visitor picks the Start navigation item',
  },
  navBuild: {
    id: 'site.nav.build',
    defaultMessage: 'Build',
    description: 'Site navigation label: open the trip builder step',
  },
  navBuildIntent: {
    id: 'site.nav.buildIntent',
    defaultMessage: 'Take me to the trip builder',
    description: 'Message sent when the visitor picks the Build navigation item',
  },
  navPlaces: {
    id: 'site.nav.places',
    defaultMessage: 'Places',
    description: 'Site navigation label: open the matched destination shortlist step',
  },
  navPlacesIntent: {
    id: 'site.nav.placesIntent',
    defaultMessage: 'Show me the destination shortlist',
    description: 'Message sent when the visitor picks the Places navigation item',
  },
  navPlan: {
    id: 'site.nav.plan',
    defaultMessage: 'Plan',
    description: 'Site navigation label: open the three-plan comparison step',
  },
  navPlanIntent: {
    id: 'site.nav.planIntent',
    defaultMessage: 'Show me the three trip plans',
    description: 'Message sent when the visitor picks the Plan navigation item',
  },
  navBooking: {
    id: 'site.nav.booking',
    defaultMessage: 'Booking',
    description: 'Site navigation label: open the transparent simulated booking-review step',
  },
  navBookingIntent: {
    id: 'site.nav.bookingIntent',
    defaultMessage: 'Show me the booking step',
    description: 'Message sent when the visitor picks the Booking navigation item',
  },
  chipDestination: {
    id: 'site.chip.findDestination',
    defaultMessage: 'Help me pick a destination',
    description: 'Standing suggestion chip: start the destination-matching conversation',
  },
  chipItinerary: {
    id: 'site.chip.planItinerary',
    defaultMessage: 'Plan a week-long trip',
    description: 'Standing suggestion chip: start planning a full itinerary',
  },
  placeholder: {
    id: 'site.placeholder',
    defaultMessage: 'Where to next?',
    description: 'Placeholder in the message input on the travel site',
  },
});

export const SITE_CONFIG: SiteConfig = {
  brandName: 'Wayfarer',
  navItems: [
    { label: siteMessages.navStart, intent: siteMessages.navStartIntent },
    { label: siteMessages.navBuild, intent: siteMessages.navBuildIntent },
    { label: siteMessages.navPlaces, intent: siteMessages.navPlacesIntent },
    { label: siteMessages.navPlan, intent: siteMessages.navPlanIntent },
    { label: siteMessages.navBooking, intent: siteMessages.navBookingIntent },
  ],
  defaultChips: [siteMessages.chipDestination, siteMessages.chipItinerary],
  mode: 'site',
  showHeader: true,
  Header: SiteHeader,
  brandedArrival: true,
  appearance: 'dark',
  placeholder: siteMessages.placeholder,
};
